import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLayers, FiSettings, FiBarChart, FiFileText, FiZap, FiShield, FiMonitor, FiUsers } = FiIcons;

const SystemArchitecture = () => {
  const modules = [
    {
      name: 'Workflow Engine',
      icon: FiSettings,
      description: 'Automated task sequences and approval chains',
      components: ['Task Templates', 'Approval Workflows', 'Notification System', 'Status Tracking'],
      integrations: ['Email Platforms', 'CMS Systems', 'Analytics Tools']
    },
    {
      name: 'SOP Library',
      icon: FiFileText,
      description: 'Comprehensive standard operating procedures',
      components: ['Step-by-Step Guides', 'Quality Checklists', 'Best Practices', 'Training Materials'],
      integrations: ['Knowledge Base', 'Video Tutorials', 'Interactive Guides']
    },
    {
      name: 'Dashboard System',
      icon: FiMonitor,
      description: 'Real-time performance monitoring and reporting',
      components: ['KPI Widgets', 'Performance Charts', 'Alert System', 'Custom Views'],
      integrations: ['Google Analytics', 'Marketing Platforms', 'CRM Systems']
    },
    {
      name: 'Automation Hub',
      icon: FiZap,
      description: 'Smart automations for repetitive tasks',
      components: ['Scheduled Tasks', 'Trigger-Based Actions', 'Data Sync', 'Report Generation'],
      integrations: ['Zapier', 'API Connections', 'Webhook Handlers']
    },
    {
      name: 'Quality Control',
      icon: FiShield,
      description: 'Validation and approval mechanisms',
      components: ['Content Review', 'Brand Compliance', 'Error Detection', 'Approval Gates'],
      integrations: ['Brand Guidelines', 'Style Checkers', 'Review Systems']
    },
    {
      name: 'Reporting Engine',
      icon: FiBarChart,
      description: 'Automated insights and performance analytics',
      components: ['Custom Reports', 'Data Visualization', 'Trend Analysis', 'Executive Summaries'],
      integrations: ['BI Tools', 'Data Warehouses', 'Export Systems']
    }
  ];

  const systemFlow = [
    { step: 1, title: 'Task Initiation', description: 'Coordinator receives or creates new task' },
    { step: 2, title: 'SOP Lookup', description: 'System provides relevant procedure guide' },
    { step: 3, title: 'Workflow Execution', description: 'Step-by-step process with checkpoints' },
    { step: 4, title: 'Quality Check', description: 'Automated validation and manual review' },
    { step: 5, title: 'Approval Process', description: 'Stakeholder review and sign-off' },
    { step: 6, title: 'Deployment', description: 'Task completion and live implementation' },
    { step: 7, title: 'Monitoring', description: 'Performance tracking and reporting' },
    { step: 8, title: 'Optimization', description: 'Continuous improvement feedback loop' }
  ];

  const integrationPoints = [
    { platform: 'Website CMS', connection: 'Direct API', purpose: 'Content management and updates' },
    { platform: 'Email Marketing', connection: 'Native Integration', purpose: 'Campaign automation and tracking' },
    { platform: 'Analytics Tools', connection: 'Data Pipeline', purpose: 'Performance monitoring and insights' },
    { platform: 'Social Media', connection: 'API Webhooks', purpose: 'Cross-channel coordination' },
    { platform: 'CRM System', connection: 'Data Sync', purpose: 'Lead tracking and attribution' },
    { platform: 'Design Tools', connection: 'Asset Bridge', purpose: 'Creative asset management' }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-4">System Architecture</h1>
        <p className="text-lg text-gray-600 max-w-3xl">
          Complete blueprint of the Web Marketing Coordinator system, showing all modules, 
          workflows, integrations, and data flows designed for maximum efficiency.
        </p>
      </motion.div>

      {/* System Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="glass-card rounded-xl p-8 mb-8"
      >
        <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
          <SafeIcon icon={FiLayers} className="w-6 h-6 text-emerald-600 mr-3" />
          Architecture Overview
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Core Architecture Principles</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2"></div>
                  <span><strong>Modular Design:</strong> Independent modules that work together seamlessly</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2"></div>
                  <span><strong>Scalable Foundation:</strong> Grows with your team and business needs</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2"></div>
                  <span><strong>Integration-First:</strong> Connects with existing marketing tools</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2"></div>
                  <span><strong>Automation-Ready:</strong> Reduces manual work through smart workflows</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 border-2 border-emerald-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">System Stats</h3>
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-600">6</div>
                <div className="text-sm text-gray-600">Core Modules</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">12</div>
                <div className="text-sm text-gray-600">SOPs Included</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">8</div>
                <div className="text-sm text-gray-600">Automated Workflows</div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Core Modules */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mb-12"
      >
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">Core System Modules</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {modules.map((module, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + index * 0.1 }}
              className="glass-card rounded-xl p-6 hover-lift"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={module.icon} className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{module.name}</h3>
              </div>
              
              <p className="text-gray-600 mb-4">{module.description}</p>
              
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Components:</h4>
                <div className="space-y-1">
                  {module.components.map((component, compIndex) => (
                    <div key={compIndex} className="text-xs text-gray-600 flex items-center space-x-1">
                      <div className="w-1 h-1 bg-emerald-500 rounded-full"></div>
                      <span>{component}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Integrations:</h4>
                <div className="flex flex-wrap gap-1">
                  {module.integrations.map((integration, intIndex) => (
                    <span key={intIndex} className="px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-full">
                      {integration}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* System Flow */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mb-12"
      >
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">System Workflow</h2>
        <div className="glass-card rounded-xl p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {systemFlow.map((item, index) => (
              <div key={index} className="workflow-step text-center">
                <div className="w-12 h-12 bg-emerald-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">
                  {item.step}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Integration Points */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="glass-card rounded-xl p-8"
      >
        <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
          <SafeIcon icon={FiUsers} className="w-6 h-6 text-emerald-600 mr-3" />
          Integration Ecosystem
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {integrationPoints.map((integration, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-900">{integration.platform}</h3>
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                  {integration.connection}
                </span>
              </div>
              <p className="text-sm text-gray-600">{integration.purpose}</p>
            </div>
          ))}
        </div>
        
        <div className="mt-8 p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Custom Integration Support</h3>
          <p className="text-gray-700">
            The system architecture supports custom integrations with your existing tools through APIs, 
            webhooks, and data pipelines. Our modular approach ensures seamless connectivity with 
            any marketing platform in your current tech stack.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default SystemArchitecture;